import { LinkedInDetails } from './ad-details/LinkedInDetails';
import { LinkedInCarouselDetails } from './ad-details/LinkedInCarouselDetails';
import type {
  LinkedInCarouselUploads,
  LinkedInCarouselInitialItem,
} from './ad-details/LinkedInCarouselDetails';
import type { FeedRatio } from './ad-details/feedCardSlots';
import { LinkedInDocumentDetails } from './ad-details/LinkedInDocumentDetails';
import { MetaSingleDetails } from './ad-details/MetaSingleDetails';
import { MetaCarouselDetails } from './ad-details/MetaCarouselDetails';
import type {
  MetaCarouselFeedUploads,
  MetaCarouselInitialItem,
} from './ad-details/MetaCarouselDetails';
import {
  MetaDynamicCreativeDetails,
  type MetaDCInitialValues,
} from './ad-details/MetaDynamicCreativeDetails';
import {
  MetaFlexibleAdDetails,
  type MetaFlexibleInitialValues,
  type FlexibleCardState,
} from './ad-details/MetaFlexibleAdDetails';
import { MetaCollectionDetails } from './ad-details/MetaCollectionDetails';
import { TikTokDetails } from './ad-details/TikTokDetails';
import { SnapchatDetails } from './ad-details/SnapchatDetails';
import { SnapchatStoryDetails } from './ad-details/SnapchatStoryDetails';
import { type MockupType, type FieldIssue } from '../types';
import { type Ratio, type UploadRatio } from '../utils';
import type { Campaign, ValidationFeedback, AssetOrderToken } from '@/api/campaigns';
import type { GXMultiSearchResponse } from '@/api/gearExchange';

export interface AdDetailsContentProps {
  campaignId?: string;
  fordshareIds?: string[] | null;
  campaign?: Campaign | null;
  mockupType: MockupType;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  selectedRatio: Ratio;
  file1x1: File | null;
  file4x5: File | null;
  file9x16: File | null;
  onFile1x1: (f: File | null) => void;
  onFile4x5: (f: File | null) => void;
  onFile9x16: (f: File | null) => void;
  /** Which of the two feed slots (1:1 / 4:5) the shared feed mockup is currently showing */
  activeFeedSlot?: '1x1' | '4x5';
  onFeedSlotSelect?: (slot: '1x1' | '4x5') => void;
  initialValues?: {
    postCopies?: string[];
    headline?: string;
    description?: string;
    cta?: string;
    url?: string;
    legal?: string;
    existingImageUrl1x1?: string;
    existingImageUrl4x5?: string;
    existingImageUrl9x16?: string;
    imageContentType?: string | null;
    imageContentType4x5?: string | null;
    imageContentType9x16?: string | null;
  };
  onMetaPostCopiesChange: (copies: string[]) => void;
  onMetaUrlChange: (url: string) => void;
  onMetaLegalChange: (legal: string) => void;
  onMetaExistingImageClear?: (ratio: UploadRatio) => void;
  onMetaCaptionChange: (v: string) => void;
  onMetaHeadlineChange: (v: string) => void;
  onMetaDescriptionChange: (v: string) => void;
  onMetaCtaChange: (v: string) => void;
  onMetaDCImageUrl1x1Change: (url: string | undefined) => void;
  onMetaDCImageUrl9x16Change: (url: string | undefined) => void;
  onMetaDCCaptionChange: (v: string) => void;
  onMetaDCHeadlineChange: (v: string) => void;
  onMetaDCDescriptionChange: (v: string) => void;
  onMetaDCCtaChange: (v: string) => void;
  onMetaDCPrimaryTextsChange?: (texts: string[]) => void;
  onMetaDCHeadlinesArrayChange?: (texts: string[]) => void;
  onMetaDCDescriptionsArrayChange?: (texts: string[]) => void;
  onMetaDCCtasArrayChange?: (ctas: string[]) => void;
  onMetaDCUrlChange?: (url: string) => void;
  onMetaDCLegalChange?: (legal: string) => void;
  onMetaDCNewFiles1x1Change?: (
    newFiles: File[],
    deletedIds: string[],
    order: AssetOrderToken[],
  ) => void;
  onMetaDCNewFiles4x5Change?: (
    newFiles: File[],
    deletedIds: string[],
    order: AssetOrderToken[],
  ) => void;
  onMetaDCNewFiles9x16Change?: (
    newFiles: File[],
    deletedIds: string[],
    order: AssetOrderToken[],
  ) => void;
  metaDCInitialValues?: MetaDCInitialValues;
  onFlexibleImageUrlChange: (url: string | undefined) => void;
  onFlexibleCaptionChange: (v: string) => void;
  onFlexibleHeadlineChange: (v: string) => void;
  onFlexibleDescriptionChange: (v: string) => void;
  onFlexibleCtaChange: (v: string) => void;
  onFlexibleUrlChange?: (v: string) => void;
  onFlexibleLegalChange?: (v: string) => void;
  onFlexibleCardsChange?: (cards: FlexibleCardState[], deletedAssetIds: string[]) => void;
  flexibleInitialValues?: MetaFlexibleInitialValues;
  onCollectionHeroFileChange: (f: File) => void;
  onCollectionHeroClear?: () => void;
  onCollectionCaptionChange: (v: string) => void;
  onCollectionCtaChange: (v: string) => void;
  onCollectionProductsChange: (products: { imageUrl?: string; title?: string }[]) => void;
  onCollectionProductFilesChange: (files: (File | null)[], deletedIds: string[]) => void;
  onCollectionWebsiteChange: (v: string) => void;
  onCollectionUrlChange: (v: string) => void;
  onCollectionLegalChange: (v: string) => void;
  collectionInitialValues?: import('./ad-details/MetaCollectionDetails').MetaCollectionInitialValues;
  metaCarouselCaption: string;
  onMetaCarouselCaptionChange: (v: string) => void;
  metaCarouselLegal: string;
  onMetaCarouselLegalChange: (v: string) => void;
  metaCarouselFeedActiveCard: number;
  metaCarouselStoryActiveCard: number;
  metaCarouselDestinationUrl: string;
  onMetaCarouselDestinationUrlChange: (v: string) => void;
  metaCarouselCta: string;
  onMetaCarouselCtaChange: (v: string) => void;
  onMetaCarouselFeedItemsChange: (
    items: {
      imageUrl?: string;
      contentType?: string | null;
      imageUrl4x5?: string;
      contentType4x5?: string | null;
      headline?: string;
      description?: string;
    }[],
  ) => void;
  onMetaCarouselStoryItemsChange: (
    items: { imageUrl?: string; contentType?: string | null }[],
  ) => void;
  onMetaCarouselSelectFeedCard: (index: number, ratio: '1x1' | '4x5') => void;
  /** Which feed crop the mockup is showing. `selectedRatio` can't carry this —
      `Ratio` has no '4x5' because both crops share the one feed mockup. */
  metaCarouselFeedSlot: '1x1' | '4x5';
  onMetaCarouselSelectStoryCard: (index: number) => void;
  onMetaCarouselFeedCardsChange: (
    uploads: MetaCarouselFeedUploads,
    deletedIds: string[],
    totalCount: number,
    cardData: { headline: string; description: string }[],
  ) => void;
  onMetaCarouselStoryCardsChange: (
    newFiles: File[],
    deletedIds: string[],
    totalCount: number,
    cardData: { headline: string; description: string }[],
    order: AssetOrderToken[],
  ) => void;
  metaCarouselFeedInitialItems?: MetaCarouselInitialItem[];
  metaCarouselStoryInitialItems?: {
    imageUrl: string;
    existingAssetId: string;
    contentType?: string | null;
  }[];
  onSnapchatHeadlineChange: (v: string) => void;
  onSnapchatUrlChange: (v: string) => void;
  onSnapchatLegalChange: (v: string) => void;
  onSnapchatExistingAssetClear?: () => void;
  snapchatActiveCard: number;
  onSnapchatStorySelectCard: (index: number) => void;
  snapchatStoryHeadline: string;
  onSnapchatStoryHeadlineChange: (v: string) => void;
  snapchatStoryUrl: string;
  onSnapchatStoryUrlChange: (v: string) => void;
  snapchatStoryLegal: string;
  onSnapchatStoryLegalChange: (v: string) => void;
  onSnapchatStoryItemsChange: (items: { imageUrl?: string; headline: string }[]) => void;
  onSnapchatStoryCardsChange: (
    newFiles: File[],
    deletedIds: string[],
    totalCount: number,
    order: AssetOrderToken[],
  ) => void;
  snapchatStoryInitialItems?: {
    imageUrl: string;
    contentType?: string | null;
    existingAssetId: string;
  }[];
  linkedInFile: File | null;
  onLinkedInFileChange: (f: File | null) => void;
  linkedInFile4x5: File | null;
  onLinkedInFile4x5Change: (f: File | null) => void;
  linkedInExistingImageUrl4x5?: string;
  activeLinkedInSlot?: '1x1' | '4x5';
  onLinkedInSlotSelect?: (slot: '1x1' | '4x5') => void;
  linkedInCaption: string;
  onLinkedInCaptionChange: (v: string) => void;
  linkedInHeadline: string;
  onLinkedInHeadlineChange: (v: string) => void;
  linkedInCta: string;
  onLinkedInCtaChange: (v: string) => void;
  onLinkedInWebsiteChange: (v: string) => void;
  onLinkedInUrlChange: (v: string) => void;
  onLinkedInLegalChange: (v: string) => void;
  onLinkedInExistingImageClear?: (ratio: UploadRatio) => void;
  linkedInDocFile: File | null;
  onLinkedInDocFileChange: (f: File | null) => void;
  linkedInDocInitialPdfUrl?: string;
  linkedInDocInitialPageImageUrl?: string;
  onLinkedInDocExistingPdfClear?: () => void;
  linkedInDocCaption: string;
  onLinkedInDocCaptionChange: (v: string) => void;
  linkedInDocHeadline: string;
  onLinkedInDocHeadlineChange: (v: string) => void;
  onLinkedInDocPageImageChange: (url: string | null) => void;
  onLinkedInDocUrlChange: (v: string) => void;
  onLinkedInDocLegalChange: (v: string) => void;
  linkedInCarouselCaption: string;
  onLinkedInCarouselCaptionChange: (v: string) => void;
  linkedInCarouselLegal: string;
  onLinkedInCarouselLegalChange: (v: string) => void;
  linkedInCarouselActiveCard: number;
  /** Which of the card's two crops the mockup is showing. */
  linkedInCarouselFeedSlot: FeedRatio;
  onLinkedInCarouselSelectCard: (index: number, ratio: FeedRatio) => void;
  onLinkedInCarouselItemsChange: (
    items: { imageUrl?: string; imageUrl4x5?: string; headline: string }[],
  ) => void;
  onLinkedInCarouselCardsChange: (
    uploads: LinkedInCarouselUploads,
    deletedIds: string[],
    totalCount: number,
    cardData: { headline: string; destinationUrl: string }[],
  ) => void;
  linkedInCarouselInitialItems?: LinkedInCarouselInitialItem[];
  onTikTokAdTextChange: (v: string) => void;
  onTikTokCtaChange: (v: string) => void;
  onTikTokUrlChange: (v: string) => void;
  onTikTokLegalChange: (v: string) => void;
  onTikTokExistingVideoClear?: () => void;
  // AI feedback props — threaded through to InlineIssue
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onAIFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  /** Map of filename → variant numbers for "used in" badges in GX modal */
  usedInVariants?: Map<string, number[]>;
  /** Gear Exchange data, fetched once and cached across variant switches */
  gxData?: GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
}

export function AdDetailsContent({
  campaignId,
  fordshareIds,
  campaign,
  mockupType,
  showErrors,
  showWarnings,
  fieldIssues,
  selectedRatio,
  file1x1,
  file4x5,
  file9x16,
  onFile1x1,
  onFile4x5,
  onFile9x16,
  activeFeedSlot,
  onFeedSlotSelect,
  initialValues,
  onMetaPostCopiesChange,
  onMetaUrlChange,
  onMetaLegalChange,
  onMetaExistingImageClear,
  onMetaCaptionChange,
  onMetaHeadlineChange,
  onMetaDescriptionChange,
  onMetaCtaChange,
  onMetaDCImageUrl1x1Change,
  onMetaDCImageUrl9x16Change,
  onMetaDCCaptionChange,
  onMetaDCHeadlineChange,
  onMetaDCDescriptionChange,
  onMetaDCCtaChange,
  onMetaDCPrimaryTextsChange,
  onMetaDCHeadlinesArrayChange,
  onMetaDCDescriptionsArrayChange,
  onMetaDCCtasArrayChange,
  onMetaDCUrlChange,
  onMetaDCLegalChange,
  onMetaDCNewFiles1x1Change,
  onMetaDCNewFiles4x5Change,
  onMetaDCNewFiles9x16Change,
  metaDCInitialValues,
  onFlexibleImageUrlChange,
  onFlexibleCaptionChange,
  onFlexibleHeadlineChange,
  onFlexibleDescriptionChange,
  onFlexibleCtaChange,
  onFlexibleUrlChange,
  onFlexibleLegalChange,
  onFlexibleCardsChange,
  flexibleInitialValues,
  onCollectionHeroFileChange,
  onCollectionHeroClear,
  onCollectionCaptionChange,
  onCollectionCtaChange,
  onCollectionProductsChange,
  onCollectionProductFilesChange,
  onCollectionWebsiteChange,
  onCollectionUrlChange,
  onCollectionLegalChange,
  collectionInitialValues,
  metaCarouselCaption,
  onMetaCarouselCaptionChange,
  metaCarouselLegal,
  onMetaCarouselLegalChange,
  metaCarouselDestinationUrl,
  onMetaCarouselDestinationUrlChange,
  metaCarouselCta,
  onMetaCarouselCtaChange,
  metaCarouselFeedActiveCard,
  metaCarouselStoryActiveCard,
  onMetaCarouselFeedItemsChange,
  onMetaCarouselStoryItemsChange,
  onMetaCarouselSelectFeedCard,
  metaCarouselFeedSlot,
  onMetaCarouselSelectStoryCard,
  onMetaCarouselFeedCardsChange,
  onMetaCarouselStoryCardsChange,
  metaCarouselFeedInitialItems,
  metaCarouselStoryInitialItems,
  onSnapchatHeadlineChange,
  onSnapchatUrlChange,
  onSnapchatLegalChange,
  onSnapchatExistingAssetClear,
  snapchatActiveCard,
  onSnapchatStorySelectCard,
  snapchatStoryHeadline,
  onSnapchatStoryHeadlineChange,
  snapchatStoryUrl,
  onSnapchatStoryUrlChange,
  snapchatStoryLegal,
  onSnapchatStoryLegalChange,
  onSnapchatStoryItemsChange,
  onSnapchatStoryCardsChange,
  snapchatStoryInitialItems,
  linkedInFile,
  onLinkedInFileChange,
  linkedInFile4x5,
  onLinkedInFile4x5Change,
  linkedInExistingImageUrl4x5,
  activeLinkedInSlot,
  onLinkedInSlotSelect,
  linkedInCaption,
  onLinkedInCaptionChange,
  linkedInHeadline,
  onLinkedInHeadlineChange,
  linkedInCta,
  onLinkedInCtaChange,
  onLinkedInWebsiteChange,
  onLinkedInUrlChange,
  onLinkedInLegalChange,
  onLinkedInExistingImageClear,
  onLinkedInDocFileChange,
  linkedInDocInitialPdfUrl,
  linkedInDocInitialPageImageUrl,
  onLinkedInDocExistingPdfClear,
  linkedInDocCaption,
  onLinkedInDocCaptionChange,
  linkedInDocHeadline,
  onLinkedInDocHeadlineChange,
  onLinkedInDocPageImageChange,
  onLinkedInDocUrlChange,
  onLinkedInDocLegalChange,
  linkedInCarouselCaption,
  onLinkedInCarouselCaptionChange,
  linkedInCarouselLegal,
  onLinkedInCarouselLegalChange,
  linkedInCarouselActiveCard,
  linkedInCarouselFeedSlot,
  onLinkedInCarouselSelectCard,
  onLinkedInCarouselItemsChange,
  onLinkedInCarouselCardsChange,
  linkedInCarouselInitialItems,
  onTikTokAdTextChange,
  onTikTokCtaChange,
  onTikTokUrlChange,
  onTikTokLegalChange,
  onTikTokExistingVideoClear,
  feedback,
  feedbackPending,
  onAIFeedback,
  usedInVariants,
  gxData,
  gxLoading,
  gxError,
}: AdDetailsContentProps) {
  switch (mockupType) {
    case 'meta-collection':
      return (
        <MetaCollectionDetails
          campaignId={campaignId}
          fordshareIds={fordshareIds}
          campaign={campaign}
          showErrors={showErrors}
          showWarnings={showWarnings}
          fieldIssues={fieldIssues}
          initialValues={collectionInitialValues}
          onHeroFileChange={onCollectionHeroFileChange}
          onHeroClear={onCollectionHeroClear}
          onCaptionChange={onCollectionCaptionChange}
          onCtaChange={onCollectionCtaChange}
          onProductsChange={onCollectionProductsChange}
          onProductFilesChange={onCollectionProductFilesChange}
          onWebsiteChange={onCollectionWebsiteChange}
          onUrlChange={onCollectionUrlChange}
          onLegalChange={onCollectionLegalChange}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onAIFeedback={onAIFeedback}
          usedInVariants={usedInVariants}
          gxData={gxData}
          gxLoading={gxLoading}
          gxError={gxError}
        />
      );
    case 'meta-dynamic-creative':
      return (
        <MetaDynamicCreativeDetails
          campaignId={campaignId}
          fordshareIds={fordshareIds}
          campaign={campaign}
          showErrors={showErrors}
          showWarnings={showWarnings}
          fieldIssues={fieldIssues}
          activeRatio={selectedRatio}
          initialValues={metaDCInitialValues}
          onImageUrl1x1Change={onMetaDCImageUrl1x1Change}
          onImageUrl9x16Change={onMetaDCImageUrl9x16Change}
          onCaptionChange={onMetaDCCaptionChange}
          onHeadlineChange={onMetaDCHeadlineChange}
          onDescriptionChange={onMetaDCDescriptionChange}
          onCtaChange={onMetaDCCtaChange}
          onPrimaryTextsChange={onMetaDCPrimaryTextsChange}
          onHeadlinesArrayChange={onMetaDCHeadlinesArrayChange}
          onDescriptionsArrayChange={onMetaDCDescriptionsArrayChange}
          onCtasArrayChange={onMetaDCCtasArrayChange}
          onUrlChange={onMetaDCUrlChange}
          onLegalChange={onMetaDCLegalChange}
          onNewFiles1x1Change={onMetaDCNewFiles1x1Change}
          onNewFiles4x5Change={onMetaDCNewFiles4x5Change}
          onNewFiles9x16Change={onMetaDCNewFiles9x16Change}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onAIFeedback={onAIFeedback}
          usedInVariants={usedInVariants}
          gxData={gxData}
          gxLoading={gxLoading}
          gxError={gxError}
        />
      );
    case 'meta-flexible-ad':
      return (
        <MetaFlexibleAdDetails
          campaignId={campaignId}
          fordshareIds={fordshareIds}
          campaign={campaign}
          initialValues={{
            ...flexibleInitialValues,
            // initialValues (from the JSX IIFE) reads directly from the assets query —
            // it's always current at render time, unlike flexibleInitialValues which is
            // state set asynchronously by a useEffect (runs after the remount).
            cta: initialValues?.cta ?? flexibleInitialValues?.cta,
            url: initialValues?.url !== undefined ? initialValues.url : flexibleInitialValues?.url,
            legal:
              initialValues?.legal !== undefined
                ? initialValues.legal
                : flexibleInitialValues?.legal,
          }}
          showErrors={showErrors}
          showWarnings={showWarnings}
          fieldIssues={fieldIssues}
          onImageUrlChange={onFlexibleImageUrlChange}
          onCaptionChange={onFlexibleCaptionChange}
          onHeadlineChange={onFlexibleHeadlineChange}
          onDescriptionChange={onFlexibleDescriptionChange}
          onCtaChange={onFlexibleCtaChange}
          onUrlChange={onFlexibleUrlChange}
          onLegalChange={onFlexibleLegalChange}
          onCardsChange={onFlexibleCardsChange}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onAIFeedback={onAIFeedback}
          usedInVariants={usedInVariants}
          gxData={gxData}
          gxLoading={gxLoading}
          gxError={gxError}
        />
      );
    case 'meta-carousel':
      return (
        <MetaCarouselDetails
          campaignId={campaignId}
          fordshareIds={fordshareIds}
          campaign={campaign}
          showErrors={showErrors}
          showWarnings={showWarnings}
          fieldIssues={fieldIssues}
          caption={metaCarouselCaption}
          onCaptionChange={onMetaCarouselCaptionChange}
          destinationUrl={metaCarouselDestinationUrl}
          onDestinationUrlChange={onMetaCarouselDestinationUrlChange}
          ctaValue={metaCarouselCta}
          onCtaChange={onMetaCarouselCtaChange}
          legal={metaCarouselLegal}
          onLegalChange={onMetaCarouselLegalChange}
          activeFeedCardIndex={metaCarouselFeedActiveCard}
          activeStoryCardIndex={metaCarouselStoryActiveCard}
          activeRatio={selectedRatio === '9x16' ? '9x16' : metaCarouselFeedSlot}
          onFeedItemsChange={onMetaCarouselFeedItemsChange}
          onStoryItemsChange={onMetaCarouselStoryItemsChange}
          onSelectFeedCard={onMetaCarouselSelectFeedCard}
          onSelectStoryCard={onMetaCarouselSelectStoryCard}
          onFeedCardsChange={onMetaCarouselFeedCardsChange}
          onStoryCardsChange={onMetaCarouselStoryCardsChange}
          initialFeedItems={metaCarouselFeedInitialItems}
          initialStoryItems={metaCarouselStoryInitialItems}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onAIFeedback={onAIFeedback}
          usedInVariants={usedInVariants}
          gxData={gxData}
          gxLoading={gxLoading}
          gxError={gxError}
        />
      );
    case 'tiktok':
      return (
        <TikTokDetails
          campaignId={campaignId}
          fordshareIds={fordshareIds}
          campaign={campaign}
          showErrors={showErrors}
          showWarnings={showWarnings}
          fieldIssues={fieldIssues}
          file9x16={file9x16}
          onFile9x16={onFile9x16}
          onAdTextChange={onTikTokAdTextChange}
          onCtaChange={onTikTokCtaChange}
          onUrlChange={onTikTokUrlChange}
          onLegalChange={onTikTokLegalChange}
          onExistingVideoClear={onTikTokExistingVideoClear}
          initialValues={{
            adText: initialValues?.postCopies?.[0],
            cta: initialValues?.cta,
            url: initialValues?.url,
            legal: initialValues?.legal,
            existingVideoUrl: initialValues?.existingImageUrl9x16,
            videoContentType:
              initialValues?.imageContentType9x16 ?? initialValues?.imageContentType,
          }}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onAIFeedback={onAIFeedback}
          usedInVariants={usedInVariants}
          gxData={gxData}
          gxLoading={gxLoading}
          gxError={gxError}
        />
      );
    case 'snapchat':
      return (
        <SnapchatDetails
          campaignId={campaignId}
          fordshareIds={fordshareIds}
          campaign={campaign}
          showErrors={showErrors}
          showWarnings={showWarnings}
          fieldIssues={fieldIssues}
          file9x16={file9x16}
          onFile9x16={onFile9x16}
          onHeadlineChange={onSnapchatHeadlineChange}
          onUrlChange={onSnapchatUrlChange}
          onLegalChange={onSnapchatLegalChange}
          onExistingAssetClear={onSnapchatExistingAssetClear}
          initialValues={{
            headline: initialValues?.headline,
            url: initialValues?.url,
            legal: initialValues?.legal,
            existingImageUrl: initialValues?.existingImageUrl9x16,
            imageContentType:
              initialValues?.imageContentType9x16 ?? initialValues?.imageContentType,
          }}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onAIFeedback={onAIFeedback}
          usedInVariants={usedInVariants}
          gxData={gxData}
          gxLoading={gxLoading}
          gxError={gxError}
        />
      );
    case 'snapchat-story':
      return (
        <SnapchatStoryDetails
          campaignId={campaignId}
          fordshareIds={fordshareIds}
          campaign={campaign}
          showErrors={showErrors}
          showWarnings={showWarnings}
          fieldIssues={fieldIssues}
          headline={snapchatStoryHeadline}
          onHeadlineChange={onSnapchatStoryHeadlineChange}
          url={snapchatStoryUrl}
          onUrlChange={onSnapchatStoryUrlChange}
          legal={snapchatStoryLegal}
          onLegalChange={onSnapchatStoryLegalChange}
          activeCardIndex={snapchatActiveCard}
          onSelectCard={onSnapchatStorySelectCard}
          onItemsChange={onSnapchatStoryItemsChange}
          onCardsChange={onSnapchatStoryCardsChange}
          initialItems={snapchatStoryInitialItems}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onAIFeedback={onAIFeedback}
          usedInVariants={usedInVariants}
          gxData={gxData}
          gxLoading={gxLoading}
          gxError={gxError}
        />
      );
    case 'linkedin-carousel':
      return (
        <LinkedInCarouselDetails
          campaignId={campaignId}
          fordshareIds={fordshareIds}
          campaign={campaign}
          showErrors={showErrors}
          showWarnings={showWarnings}
          fieldIssues={fieldIssues}
          caption={linkedInCarouselCaption}
          onCaptionChange={onLinkedInCarouselCaptionChange}
          legal={linkedInCarouselLegal}
          onLegalChange={onLinkedInCarouselLegalChange}
          activeCardIndex={linkedInCarouselActiveCard}
          activeRatio={linkedInCarouselFeedSlot}
          onSelectCard={onLinkedInCarouselSelectCard}
          onItemsChange={onLinkedInCarouselItemsChange}
          onCardsChange={onLinkedInCarouselCardsChange}
          initialItems={linkedInCarouselInitialItems}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onAIFeedback={onAIFeedback}
          usedInVariants={usedInVariants}
          gxData={gxData}
          gxLoading={gxLoading}
          gxError={gxError}
        />
      );
    case 'linkedin-document':
      return (
        <LinkedInDocumentDetails
          showErrors={showErrors}
          showWarnings={showWarnings}
          fieldIssues={fieldIssues}
          caption={linkedInDocCaption}
          onCaptionChange={onLinkedInDocCaptionChange}
          headline={linkedInDocHeadline}
          onHeadlineChange={onLinkedInDocHeadlineChange}
          onFileChange={onLinkedInDocFileChange}
          onPageImageChange={onLinkedInDocPageImageChange}
          onUrlChange={onLinkedInDocUrlChange}
          onLegalChange={onLinkedInDocLegalChange}
          initialValues={{
            url: initialValues?.url,
            legal: initialValues?.legal,
          }}
          initialPdfUrl={linkedInDocInitialPdfUrl}
          initialPageImageUrl={linkedInDocInitialPageImageUrl}
          onExistingPdfClear={onLinkedInDocExistingPdfClear}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onAIFeedback={onAIFeedback}
        />
      );
    case 'linkedin':
      return (
        <LinkedInDetails
          campaignId={campaignId}
          fordshareIds={fordshareIds}
          campaign={campaign}
          showErrors={showErrors}
          showWarnings={showWarnings}
          fieldIssues={fieldIssues}
          file1x1={linkedInFile}
          onFile1x1={onLinkedInFileChange}
          file4x5={linkedInFile4x5}
          onFile4x5={onLinkedInFile4x5Change}
          activeSlot={activeLinkedInSlot}
          onSlotSelect={onLinkedInSlotSelect}
          caption={linkedInCaption}
          onCaptionChange={onLinkedInCaptionChange}
          headline={linkedInHeadline}
          onHeadlineChange={onLinkedInHeadlineChange}
          cta={linkedInCta}
          onCtaChange={onLinkedInCtaChange}
          onWebsiteChange={onLinkedInWebsiteChange}
          onUrlChange={onLinkedInUrlChange}
          onLegalChange={onLinkedInLegalChange}
          onExistingImageClear={onLinkedInExistingImageClear}
          initialValues={{
            existingImageUrl: initialValues?.existingImageUrl1x1,
            imageContentType: initialValues?.imageContentType,
            existingImageUrl4x5: linkedInExistingImageUrl4x5,
            imageContentType4x5: initialValues?.imageContentType4x5,
            url: initialValues?.url,
            legal: initialValues?.legal,
          }}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onAIFeedback={onAIFeedback}
          usedInVariants={usedInVariants}
          gxData={gxData}
          gxLoading={gxLoading}
          gxError={gxError}
        />
      );
    default:
      return (
        <MetaSingleDetails
          campaignId={campaignId}
          fordshareIds={fordshareIds}
          campaign={campaign}
          showErrors={showErrors}
          showWarnings={showWarnings}
          fieldIssues={fieldIssues}
          file1x1={file1x1}
          file4x5={file4x5}
          file9x16={file9x16}
          onFile1x1={onFile1x1}
          onFile4x5={onFile4x5}
          onFile9x16={onFile9x16}
          activeFeedSlot={activeFeedSlot}
          onFeedSlotSelect={onFeedSlotSelect}
          onExistingImageClear={(ratio) => onMetaExistingImageClear?.(ratio)}
          onCaptionChange={onMetaCaptionChange}
          onHeadlineChange={onMetaHeadlineChange}
          onDescriptionChange={onMetaDescriptionChange}
          onCtaChange={onMetaCtaChange}
          onPostCopiesChange={onMetaPostCopiesChange}
          onUrlChange={onMetaUrlChange}
          onLegalChange={onMetaLegalChange}
          initialValues={initialValues}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onAIFeedback={onAIFeedback}
          usedInVariants={usedInVariants}
          gxData={gxData}
          gxLoading={gxLoading}
          gxError={gxError}
        />
      );
  }
}
